/**
 * Custom functions to be used as Vue filters in templates or as standalone functions
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2019
 */

import moment from 'moment';
import 'moment-duration-format';
import 'twix';
import plural from 'pluralize';

/**
 * Formats a given date
 * @param {String} value - the date to format
 * @param {String} [format='YYYY-MM-DDTHH:mm:ss'] - MomentJS formatting notation
 * @param {Boolean} [local=false] - Use browser's local timezone, otherwise use UTC
 */
export function formatDate(value, format = 'YYYY-MM-DDTHH:mm:ss', local = false) {
  const formattedDate = local ? moment(value).format(format) : moment.utc(value).format(format);
  return value ? formattedDate : value;
}

/**
 * Transforms a given date into the timeago/relative format
 * @param {String} value - the date to format
 * @param {Boolean} [local=false] - Use browser's local timezone, otherwise use UTC
 */
export function formatTimeago(value, local = false) {
  const dt = local ? moment(value) : moment.utc(value);
  return value && dt.isValid() ? dt.fromNow() : value;
}

/**
 * Formats a given number of seconds into a human-friendly duration
 * @param {Number|String} seconds
 * @param {String} [format] - moment-duration-format accepted formatting template
 * @returns {String}
 */
export function formatDuration(seconds, format = 'D[d] H[h] m[m] s[s]') {
  const num = parseInt(seconds, 10);
  return !isNaN(num) && num ? moment.duration(num, 'seconds').format(format) : seconds;
}

/**
 * Parses and formats a given ISO8601 datetime taking into consideration the timezone offset
 * @param {String} value - the date to format, must be an ISO8601 string
 * @param {String} [format='h:mm A'] - MomentJS formatting notation
 * @returns {Undefined}
 */
export function formatDateWithZone(value, format = 'h:mm A') {
  const parsedDate = moment.parseZone(value, moment.ISO_8601);
  return parsedDate.isValid() ? parsedDate.format(format) : value;
}

/**
 * Sets a friendly range between two dates or just formats the start date
 * @param {string} startDate - with the YYYY-MM-DDTHH:mm:ss format
 * @param {string} [endDate] - with the YYYY-MM-DDTHH:mm:ss format
 * @returns {<string|null>} - Returns either the range, just the formated start date, or null for invalid params
 */
export function formatDateRange(startDate, endDate, inputFormat = 'YYYY-MM-DDTHH:mm:ss') {
  const start = moment(startDate, inputFormat);
  const end = moment(endDate, inputFormat);
  let range = null;

  if (start.isValid()) {
    // either set the range or just the start date
    if (end.isValid()) {
      range = moment(start, inputFormat).twix(end, {
        allDay: true,
      });

      // format twix range using it's options
      range = range.format({
        monthFormat: 'MMMM',
        yearFormat: 'YYYY',
        dayFormat: 'D',
        implicitYear: false,
        template: function formatTemplate(left, right) {
          return left + ' to ' + right; // encoded
        },
      });
    } else {
      range = start.format('MMMM D, YYYY');
    }
  }

  return range;
}

/**
 * Takes a given word and count and makes it plural if needed
 * @param {Number} count - number of items that were checking the word against
 * @param {String} word - word to be pluralized
 * @param {Boolean} [includeCount=true] - show the count in the pluralized result
 * @returns {String}
 */
export function pluralize(count, word, includeCount = true) {
  return plural(word, count, includeCount);
}

/**
 * Formats a number into language sensitive string with appropriate separators
 * @param {number|string} [num=0]
 * @param {object} [options={}] - Intl.NumberFormat options
 * @returns {String}
 */
export function formatNumber(num = 0, options = {}) {
  let parsedNum = num;
  if (typeof num === 'string') {
    parsedNum = parseInt(num, 10);
  }

  parsedNum = isNaN(parsedNum) ? 0 : parsedNum;
  return new Intl.NumberFormat('en-US', options).format(parsedNum);
}

export function formatNumberCompact(num = 0) {
  let fractionDigits = 0;
  if (num >= 1000000) {
    fractionDigits = 1;
  }

  return formatNumber(num, {
    notation: 'compact',
    maximumFractionDigits: fractionDigits,
  });
}

export function formatNumberLimit(num, limit = 100000) {
  if (num < limit) {
    return formatNumber(num);
  }

  return formatNumberCompact(num);
}
